import Grid from '@mui/material/Grid';
import classNames from 'classnames';
import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import Draggable from 'react-draggable';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import NewItemOverlay from '/@/components/map/new-item-overlay';
import Tab from '/@/components/new-tab';
import Tabs from '/@/components/new-tabs';
import Spinner from '/@/components/spinner';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { setVersionLabel } from '/@/store/maps/actions';
import { getVersionLabel } from '/@/store/maps/selectors';
import Select from '/@/v3/components/forms/select';
import styles from './styles.module.css';

const tabs = () => {
    return [
        {
            icon: 'corn',
            label: _('Maíz'),
            children: [
                { label: _('Enfermedades'), type: 'corn_diseases_historic_pressure' },
                { label: _('Plagas'), type: 'corn_pests_historic_pressure' },
                { label: _('Caracteres Agrónomicos'), type: 'corn_others' },
            ],
        },
        { icon: 'weeds', label: _('Malezas'), type: 'weeds_rem' },
        { icon: 'alert', label: _('Pronóstico de Spodoptera'), type: 'spodoptera_forecast' },
        { icon: 'humidity', label: _('Humedad en el suelo'), type: 'humidity' },
    ];
};

@connect(
    (state) => ({ versionLabel: getVersionLabel(state) }),
    (dispatch) => ({ ...bindActionCreators({ setVersionLabel }, dispatch), dispatch })
)
@autobind
export default class NewOverlayListMobile extends Component {
    static propTypes = {
        selected: PropTypes.object,
        field: PropTypes.object,
        overlays: PropTypes.array,
        squashSingle: PropTypes.bool,
        onOverlayClick: PropTypes.func,
        tabs: PropTypes.func,
    };

    static defaultProps = {
        onOverlayClick: () => null,
        tabs,
    };

    state = {
        tab: undefined,
        weedValue: '',
        listValue: '',
        listOptions: [],
        expanded: true,
    };

    componentDidUpdate(prevProps) {
        if (this.props.overlays !== prevProps.overlays) {
            const tree = this.getTree();
            this.handleTabClick(tree?.[0]?.id);
        }
    }

    validateOverlay(overlay) {
        if (!this.props.field) return true;
        const { field } = this.props;

        return overlay.versions
            .flatMap(({ referenceItems }) => referenceItems)
            .some(({ id: refId }) => field.mapValues.find(({ id }) => id === refId));
    }

    buildTree(items, item, level) {
        if (!item.type && !item.children) throw new Error('Invalid overlay items configuration.');

        const { squashSingle, selected = { overlay: {} } } = this.props;

        item.id = `${_(item.label)}-${level}`; // Used as key for components

        item.level = level;
        item.children = item.children
            ? item.children.reduce((children, child) => this.buildTree(children, child, level + 1), [])
            : [];

        let overlays = [];

        if (item.type) {
            overlays = this.props.overlays.filter(
                (overlay) => overlay.type === item.type && this.validateOverlay(overlay)
            );
        }

        if (!squashSingle || overlays.length !== 1) {
            // Overlay List
            item.children.push(
                ...overlays
                    .sort((a, b) => a.order - b.order)
                    .map((overlay) => {
                        const current = selected && selected.overlay.slug == overlay.slug;
                        return {
                            id: `${_(overlay.slug)}-${level}`,
                            level: level + 1,
                            label: _(overlay.name),
                            selected: current,
                            onClick: this.handleOverlayClick(!current ? overlay : undefined),
                            overlay,
                        };
                    })
            );
        } else {
            // Direct Link
            item.selected = selected && selected.overlay.slug == overlays?.[0]?.slug;
            item.onClick = this.handleOverlayClick(!item.selected ? overlays[0] : undefined);
        }

        // Handle expanded drilldown
        item.expanded = item.children.some(({ expanded, selected }) => Boolean(expanded) || Boolean(selected));

        if (!item.children.length && !item.onClick) return items;

        items.push(item);

        return items;
    }

    getTree() {
        return this.props.tabs().reduce((items, item) => this.buildTree(items, item, 0), []);
    }

    handleOverlayClick(overlay) {
        return () => {
            const data = overlay && { type: overlay.slug, version: overlay.versions?.[0]?.versionNumber };

            this.props.onOverlayClick(data);
        };
    }

    handleTabClick(v) {
        const overlay = this.getTree().find((t) => t.id === v);

        if (overlay.children.length === 0) {
            overlay.onClick();
        } else {
            this.props.onOverlayClick(overlay);
        }
        this.showListSelect();
        this.setState({ tab: v, listOptions: [], weedValue: '', listValue: '' });
    }

    handleWeedChange(v, selected) {
        this.setState({ weedValue: v });
        const selectedWeed = selected.children.find((s) => {
            return s.id === v;
        });
        let listOptions;
        if (selectedWeed.children) {
            listOptions = selectedWeed.children.map((o) => {
                return {
                    label: o.label,
                    value: o.id,
                };
            });
        } else {
            let data = selectedWeed.overlay;
            data = { ...data, type: data.slug, version: data.versions?.[0]?.versionNumber };

            if (this.props.versionLabel && data) {
                const sameLabelVersion = data.versions.find((v) => _(v.versionLabel) === this.props.versionLabel);
                if (sameLabelVersion) {
                    data.version = sameLabelVersion.versionNumber;
                } else {
                    this.props.setVersionLabel(_(data.versions?.[0]?.versionLabel));
                }
            }

            this.props.handleOverlayChange(data);
        }

        this.setState({ listOptions });
    }

    showListSelect(selected) {
        return Boolean(
            selected &&
                selected.children.length &&
                selected.children.every((s) => {
                    return Object.keys(s).includes('children');
                })
        );
    }

    handleListChange(v, selectedList) {
        this.setState({ listValue: v });
        let data =
            selectedList &&
            selectedList.children.find((c) => {
                return c.id === v;
            });
        data = data.overlay;
        data = { ...data, type: data.slug, version: data.versions?.[0]?.versionNumber };

        if (this.props.versionLabel && data) {
            const sameLabelVersion = data.versions.find((v) => _(v.versionLabel) === this.props.versionLabel);
            if (sameLabelVersion) {
                data.version = sameLabelVersion.versionNumber;
            } else {
                this.props.setVersionLabel(_(data.versions?.[0]?.versionLabel));
            }
        }

        this.props.handleOverlayChange(data);
    }

    render() {
        const tree = this.getTree();
        const selected = this.getTree().find((t) => t.id === this.state.tab);
        if (!selected)
            return (
                <Grid
                    container
                    justifyContent='center'
                    alignItems='center'
                    sx={{
                        height: '100%',
                        background: 'black',
                        position: 'relative',
                        zIndex: 100,
                        filter: 'opacity(0.2)',
                    }}
                >
                    <Grid item sx={{}}>
                        <Spinner />
                    </Grid>
                </Grid>
            );

        const selectedList = selected.children.find((s) => s.id === this.state.weedValue);

        const weedOptions =
            selected &&
            selected.children.map((o) => {
                return {
                    label: o.label,
                    value: o.id,
                };
            });

        return (
            <Fragment>
                {this.props.overlay && (
                    <div
                        className={classNames(styles.button, this.state.expanded ? styles.opened : styles.closed)}
                        onClick={() => this.setState({ expanded: !this.state.expanded })}
                    >
                        <span className='material-icons'>{this.state.expanded ? 'expand_more' : 'expand_less'}</span>
                    </div>
                )}

                <div
                    className={classNames(styles.overlay, this.state.expanded ? styles.expanded : styles.closedOverlay)}
                >
                    {this.state.expanded && (
                        <Tabs
                            value={this.state.tab}
                            onChange={(ev, v) => this.handleTabClick(v)}
                            textColor='primary'
                            indicatorColor='primary'
                            aria-label='tabs'
                            centered
                            sx={{ width: '100%' }}
                        >
                            {tree.map((tab) => {
                                return <Tab minWidth value={tab.id} label={_(tab.label)} wrapped />;
                            })}
                        </Tabs>
                    )}

                    <Grid container>
                        {((weedOptions && Boolean(weedOptions.length)) || this.showListSelect(selected)) &&
                            this.state.expanded && (
                                <Grid
                                    container
                                    justifyContent='space-between'
                                    spacing={2}
                                    xs={12}
                                    sx={{ p: 3, borderBottom: '1px solid #e8e8e8' }}
                                >
                                    {weedOptions && Boolean(weedOptions.length) && (
                                        <Grid item xs={this.showListSelect(selected) ? 6 : 12}>
                                            <Select
                                                color={settings.app.theme}
                                                appearance='outline'
                                                value={this.state.weedValue}
                                                options={weedOptions}
                                                onChange={(v) => this.handleWeedChange(v, selected, selectedList)}
                                                placeholder={_('Enfermedades')}
                                                className={styles.select}
                                                maxHeight='200px'
                                            />
                                        </Grid>
                                    )}
                                    {this.showListSelect(selected) && (
                                        <Grid item xs={6}>
                                            <Select
                                                color={settings.app.theme}
                                                appearance='outline'
                                                value={this.state.listValue}
                                                options={this.state.listOptions ? this.state.listOptions : weedOptions}
                                                onChange={(v) => this.handleListChange(v, selectedList)}
                                                placeholder={_('Listado')}
                                                className={styles.select}
                                                disabled={!this.state.listOptions.length}
                                                maxHeight='200px'
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            )}

                        {this.props.overlay && (
                            <Draggable bounds='parent' handle='.handle'>
                                <NewItemOverlay
                                    expanded={this.state.expanded}
                                    {...this.props.overlay}
                                    onOverlayChange={this.props.handleOverlayChange}
                                    tab={this.state.tab}
                                />
                            </Draggable>
                        )}
                    </Grid>
                </div>
            </Fragment>
        );
    }
}
